import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { forkJoin, merge, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { FlowStorage, PaymentBrowserBackStorage } from '@pe/checkout/storage';
import { CheckoutWindow, FlowInterface, FlowStateEnum, PaymentMethodEnum, PaymentStatusEnum } from '@pe/checkout/types';

import { SetPaymentComplete } from '../checkout';

import { CloneFlow, InitFlow } from './flow.actions';
import { FlowState } from './flow.state';

@Injectable({
  providedIn: 'root',
})
export class FlowActionHandler implements OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private readonly loader = (window as CheckoutWindow).pe_pageFlowLoader;

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly flowStorage: FlowStorage,
    private readonly injector: Injector,
  ) {
    this.initLoader();

    const cloneFlow$ = forkJoin({
      flow: this.store.selectOnce(FlowState.flow),
      action: this.actions$.pipe(
        ofActionSuccessful(CloneFlow),
      ),
    }).pipe(
      tap(({ flow, action }) => {
        if (!action.payload.skipData) {
          const clonedFlowId = this.store.selectSnapshot(FlowState.flowId);
          this.flowStorage.clone(flow.id, clonedFlowId);
        }
      }),
    );

    merge(
      cloneFlow$,
    ).pipe(
      takeUntil(this.destroy$),
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initLoader(): void {
    const initFlow = (flowData: FlowInterface) => {
      flowData = this.restoreCheckoutAfterBack(flowData);
      this.store.dispatch(new InitFlow(flowData));
      const isFlowHasFinishedPayment = Boolean(
        flowData
        && [FlowStateEnum.FINISH, FlowStateEnum.CANCEL].indexOf(flowData.state) >= 0
      );
      isFlowHasFinishedPayment && this.store.dispatch(new SetPaymentComplete());
    };

    if (this.loader?.flowData) {
      initFlow(this.loader.flowData);
    } else if (this.loader) {
      this.loader.successCallback = (data: FlowInterface) => {
        initFlow(data);
      };

      this.loader.errorCallback = (message) => {
        this.handleError();
      };

      if (this.loader.lastError) {
        this.handleError();
      }
    }
  }

  private restoreCheckoutAfterBack(flow: FlowInterface): FlowInterface {
    const method = flow?.paymentOptions?.find(option =>
      option.connections?.some(connection => connection.id === flow.connectionId))?.paymentMethod;
    if (method !== PaymentMethodEnum.SANTANDER_INSTALLMENT_AT || flow.state !== FlowStateEnum.FINISH) {
      return flow;
    }

    const browserBackStorage = this.injector.get(PaymentBrowserBackStorage);
    const response = this.flowStorage.getData(flow.id, 'paymentData')?.[method]?.[flow.connectionId]?.response;
    const unfinished = [PaymentStatusEnum.STATUS_NEW, PaymentStatusEnum.STATUS_IN_PROCESS];
    if (!response?.id || !unfinished.includes(flow.payment?.status ?? response.payment?.status)) {
      browserBackStorage.clear(flow.id);

      return flow;
    }

    const url = new URL(window.location.href);
    if (!url.pathname.endsWith(`/pay/${flow.id}`) || url.searchParams.has('processed')
      || !browserBackStorage.hasReturned(flow.id, flow.connectionId, response.id)) {
      return flow;
    }

    return { ...flow, state: FlowStateEnum.PROGRESS };
  }

  private handleError(): void {
    return;
  }
}
